import { useIntl } from 'react-intl';
import { useLocation, useHistory } from 'react-router-dom';
import { useCallback, useMemo, useRef, useEffect } from 'react';
import { SearchView } from 'volto-ministerio-publico-federal/components';
import { useSearchResults } from 'volto-ministerio-publico-federal/hooks';
import { flattenToAppURL, getBaseUrl } from '@plone/volto/helpers';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import qs from 'query-string';
import config from '@plone/volto/registry';
import {
  PORTAL_TYPES,
  getDateFilterOptions,
} from 'volto-ministerio-publico-federal/components/View/SearchView/config';
import type {
  SearchState,
  SearchResultItem,
} from 'volto-ministerio-publico-federal/components/View/SearchView/types';

const SearchComponent = () => {
  const intl = useIntl();
  const location = useLocation();
  const history = useHistory();
  const isHydrating = useRef(true);

  const messages = {
    Search: { id: 'Search', defaultMessage: 'Search' },
  };
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const hash = window.location.hash;
    const currentParams = qs.parse(location.search);

    // Trigger: hash is present AND Subject is missing/empty in parsed query string
    if (hash && (!currentParams.Subject || currentParams.Subject === '')) {
      const fragment = hash.substring(1); // Remove the leading #
      if (fragment) {
        // Reconstruct URL with Subject containing the fragment content
        currentParams.Subject = '#' + fragment;
        history.replace({
          search: qs.stringify(currentParams),
          hash: '', // Clear the fragment from the URL
        });
      }
    }
  }, [location.search, history]);
  const {
    items,
    itemsTotal,
    batching,
    loading,
    currentPage,
    handlePaginationChange,
  } = useSearchResults();

  useEffect(() => {
    isHydrating.current = false;
  }, []);

  const defaultPageSize = config.settings?.defaultPageSize || 25;
  const options = useMemo(() => qs.parse(location.search), [location.search]);

  const searchableText = useMemo(
    () => (options.SearchableText as string) || '',
    [options.SearchableText],
  );

  const results: SearchResultItem[] = useMemo(
    () =>
      items.map((item) => ({
        id: item['@id'] || '',
        title: item.title || '',
        // O catálogo devolve o `@id` absoluto da API; sem achatar, o link
        // aponta para fora da app atrás de proxy ou de prefixo `++api++`.
        url: item['@id'] ? flattenToAppURL(item['@id']) : '',
        description: item.description || '',
        type: item['@type'] || '',
        reviewState: item.review_state
          ? `state-${item.review_state}`
          : undefined,
        location: (item.breadcrumbs || []).map((crumb) => ({
          ...crumb,
          url: crumb.url ? flattenToAppURL(crumb.url) : crumb.url,
        })),
        _raw: item,
      })),
    [items],
  );

  const rssUrl = useMemo(() => {
    const baseUrl = getBaseUrl('');
    return `${baseUrl}/search_rss${location.search ? `?${location.search}` : ''}`;
  }, [location.search]);

  const initialState = useMemo((): Partial<SearchState> => {
    const defaultPortalTypes = PORTAL_TYPES.map((type) => type.value);
    return {
      searchText: searchableText,
      selectedTypes:
        (options['portal_type:list'] as string[] | undefined) ||
        defaultPortalTypes,
      selectedDate: (options['effective.query'] as string) || '1970-01-02',
      sortBy: (options.sort_on as string) || 'relevance',
      sortOrder: (options.sort_order as string) || '',
      isFilterExpanded: false,
    };
  }, [searchableText, options]);

  const handleSearchSubmit = useCallback(
    (state: SearchState) => {
      const query: Record<string, any> = {};

      if (state.searchText) {
        query.SearchableText = state.searchText;
      }

      if (state.selectedTypes.length > 0) {
        query['portal_type:list'] = state.selectedTypes;
      }

      if (state.selectedDate && state.selectedDate !== '1970-01-02') {
        query['effective.query'] = state.selectedDate;
        query['effective.range'] = 'min';
      }

      if (state.sortBy && state.sortBy !== 'relevance') {
        query.sort_on = state.sortBy;
        query.sort_order = state.sortOrder || 'ascending';
      }

      if (options.Subject) {
        query.Subject = options.Subject;
      }

      history.push({ search: qs.stringify(query) });
    },
    [history, options.Subject],
  );

  const handleSortChange = useCallback(
    (sortBy: string, sortOrder: string) => {
      const query = qs.parse(location.search);
      if (sortBy === 'relevance') {
        delete query.sort_on;
        delete query.sort_order;
      } else {
        query.sort_on = sortBy;
        query.sort_order = sortOrder || 'ascending';
      }
      if (options.Subject) {
        query.Subject = options.Subject;
      }
      history.replace({ search: qs.stringify(query) });
    },
    [location.search, history, options.Subject],
  );

  return (
    <>
      <Helmet title={intl.formatMessage(messages.Search)} />
      <SearchView
        totalResults={itemsTotal}
        results={results}
        rssUrl={rssUrl}
        onSearchSubmit={handleSearchSubmit}
        currentPage={currentPage}
        defaultPageSize={defaultPageSize}
        batching={batching}
        onPaginationChange={handlePaginationChange}
        onSortChange={handleSortChange}
        loading={loading}
        initialState={initialState}
        portalTypes={PORTAL_TYPES}
        dateOptions={getDateFilterOptions()}
      />
    </>
  );
};

export { SearchComponent as Search };

export default SearchComponent;
